import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, Badge, IconButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Input, Select, FormControl, FormLabel, HStack, VStack, Tabs, TabList, Tab, TabPanels, TabPanel,
  useToast, useDisclosure, Spinner, Tooltip, NumberInput, NumberInputField,
  SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Divider, Alert, AlertIcon, Switch,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiArrowLeft, FiRefreshCw, FiDollarSign, FiUser,
         FiList, FiCheck, FiSettings, FiInfo } from 'react-icons/fi';
import api from '../api';

const fmt = (n) => '£' + parseFloat(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = (n) => '£' + parseFloat(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const STATUS_COLOUR = { draft: 'gray', processing: 'blue', completed: 'green', cancelled: 'red' };

// ── Tax Breakdown Preview ─────────────────────────────────────────
function TaxPreview({ grossAnnual, taxCode, niCategory, pensionEE, pensionER }) {
  const [calc, setCalc] = useState(null);
  useEffect(() => {
    if (!grossAnnual) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/salary/calculate', {
          gross_annual: grossAnnual, tax_code: taxCode, ni_category: niCategory,
          pension_employee_pct: pensionEE, pension_employer_pct: pensionER,
        });
        setCalc(data);
      } catch(e) {}
    }, 500);
    return () => clearTimeout(t);
  }, [grossAnnual, taxCode, niCategory, pensionEE, pensionER]);

  if (!grossAnnual || !calc) return null;
  const rows = [
    ['Monthly Gross', calc.monthly_gross, 'blue.700'],
    ['PAYE Tax', calc.monthly_tax, 'red.600'],
    ['EE NI', calc.monthly_ee_ni, 'orange.600'],
    ['EE Pension', calc.monthly_ee_pension, 'purple.600'],
    ['Monthly Net', calc.monthly_net, 'green.700'],
    ['ER NI (employer)', calc.monthly_er_ni, 'gray.500'],
    ['ER Pension (employer)', calc.monthly_er_pension, 'gray.500'],
    ['Total Employer Cost/mo', calc.total_employer_cost_monthly, 'red.800'],
  ];
  return (
    <Box bg="blue.50" p={3} borderRadius="md" mt={2} border="1px solid" borderColor="blue.200">
      <Text fontSize="10px" fontWeight="700" color="blue.700" mb={2}>Monthly Breakdown (estimated, 2024/25 rates)</Text>
      <SimpleGrid columns={4} spacing={2}>
        {rows.map(([label, val, color]) => (
          <Box key={label} textAlign="center">
            <Text fontSize="8px" color="gray.500">{label}</Text>
            <Text fontSize="11px" fontWeight="700" color={color}>{fmt(val)}</Text>
          </Box>
        ))}
      </SimpleGrid>
      <Text fontSize="8px" color="gray.400" mt={2}>* Always verify with HMRC. Tax rates are 2024/25 England/Wales.</Text>
    </Box>
  );
}

// ── Employee Form Modal ───────────────────────────────────────────
function EmployeeForm({ isOpen, onClose, onSaved, editRecord, contacts }) {
  const BLANK = { user_id: '', gross_annual: '', tax_code: '1257L', ni_category: 'A', pay_frequency: 'monthly', pay_day_of_month: 25, start_date: new Date().toISOString().split('T')[0], bank_account_id: '', notes: '', pension_employee_pct: 5, pension_employer_pct: 3, pension_provider: '', pension_ref: '', auto_enrolled: true, auto_pay_run: false };
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const toast = useToast();
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isOpen) setForm(editRecord ? { ...BLANK, ...editRecord, user_id: editRecord.user_id || '', start_date: editRecord.start_date?.split('T')[0] || '' } : BLANK);
  }, [isOpen, editRecord]); // eslint-disable-line

  useEffect(() => {
    if (form.user_id) api.get(`/bank-accounts?user_id=${form.user_id}`)
    else if (form.contact_id) api.get(`/bank-accounts?contact_id=${form.contact_id}`).then(r => setBankAccounts(r.data)).catch(() => setBankAccounts([]));
  }, [form.contact_id]);

  const save = async () => {
    if ((!form.user_id && !form.contact_id) || !form.gross_annual) { toast({ title: 'Employee and salary required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      editRecord ? await api.put(`/salary/${editRecord.id}`, form) : await api.post('/salary', form);
      toast({ title: 'Saved', status: 'success', duration: 1500 });
      onSaved(); onClose();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  const f = form;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">{editRecord ? 'Edit Salary Record' : 'Add Employee'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs size="sm">
            <TabList><Tab fontSize="11px">Salary & Tax</Tab><Tab fontSize="11px">Pension</Tab><Tab fontSize="11px">Bank Account</Tab></TabList>
            <TabPanels>
              {/* Salary & Tax */}
              <TabPanel px={0}>
                <VStack spacing={3} align="stretch">
                  <FormControl isRequired>
                    <FormLabel fontSize="11px">Employee (User Account)</FormLabel>
                    <Select size="sm" value={f.user_id} onChange={e => set('user_id', e.target.value)} isDisabled={!!editRecord}>
                      <option value="">Select employee...</option>
                      {contacts.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}{u.email ? ` — ${u.email}` : ''}</option>)}
                    </Select>
                  </FormControl>
                  <HStack>
                    <FormControl isRequired flex={2}>
                      <FormLabel fontSize="11px">Gross Annual Salary (£)</FormLabel>
                      <NumberInput size="sm" value={f.gross_annual} onChange={v => set('gross_annual', v)} min={0} precision={2}>
                        <NumberInputField placeholder="e.g. 35000" />
                      </NumberInput>
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="11px">UK Tax Code</FormLabel>
                      <Input size="sm" value={f.tax_code} onChange={e => set('tax_code', e.target.value.toUpperCase())} placeholder="1257L" />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="11px">NI Cat.</FormLabel>
                      <Select size="sm" value={f.ni_category} onChange={e => set('ni_category', e.target.value)}>
                        {['A','B','C','D','E','F','H','I','J','K','L','M','S','V','X','Z'].map(c => <option key={c}>{c}</option>)}
                      </Select>
                    </FormControl>
                  </HStack>
                  <HStack>
                    <FormControl flex={2}>
                      <FormLabel fontSize="11px">Pay Frequency</FormLabel>
                      <Select size="sm" value={f.pay_frequency} onChange={e => set('pay_frequency', e.target.value)}>
                        <option value="monthly">Monthly</option><option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly</option><option value="four_weekly">4-Weekly</option>
                      </Select>
                    </FormControl>
                    {f.pay_frequency === 'monthly' && (
                      <FormControl flex={1}>
                        <FormLabel fontSize="11px">Pay Day</FormLabel>
                        <NumberInput size="sm" value={f.pay_day_of_month} onChange={v => set('pay_day_of_month', v)} min={1} max={31}>
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>
                    )}
                    <FormControl flex={2}>
                      <FormLabel fontSize="11px">Start Date</FormLabel>
                      <Input size="sm" type="date" value={f.start_date} onChange={e => set('start_date', e.target.value)} />
                    </FormControl>
                  </HStack>
                  <HStack align="center">
                    <Switch size="sm" isChecked={!!f.auto_pay_run} onChange={e => set('auto_pay_run', e.target.checked)} colorScheme="blue" />
                    <Text fontSize="11px">Include in automated pay runs</Text>
                  </HStack>
                  <FormControl><FormLabel fontSize="11px">Notes</FormLabel><Input size="sm" value={f.notes} onChange={e => set('notes', e.target.value)} /></FormControl>
                  <TaxPreview grossAnnual={f.gross_annual} taxCode={f.tax_code} niCategory={f.ni_category} pensionEE={f.pension_employee_pct} pensionER={f.pension_employer_pct} />
                </VStack>
              </TabPanel>
              {/* Pension */}
              <TabPanel px={0}>
                <VStack spacing={3} align="stretch">
                  <Alert status="info" fontSize="11px" borderRadius="md"><AlertIcon />Auto-enrolment minimum: 5% employee, 3% employer (2024/25). Based on qualifying earnings £6,240–£50,270.</Alert>
                  <HStack align="center">
                    <Switch size="sm" isChecked={!!f.auto_enrolled} onChange={e => set('auto_enrolled', e.target.checked)} colorScheme="green" />
                    <Text fontSize="11px" fontWeight="600">Auto-enrolled in workplace pension</Text>
                  </HStack>
                  {f.auto_enrolled && (
                    <>
                      <HStack>
                        <FormControl>
                          <FormLabel fontSize="11px">Employee Contribution %</FormLabel>
                          <NumberInput size="sm" value={f.pension_employee_pct} onChange={v => set('pension_employee_pct', parseFloat(v))} min={0} max={100} precision={2}>
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="11px">Employer Contribution %</FormLabel>
                          <NumberInput size="sm" value={f.pension_employer_pct} onChange={v => set('pension_employer_pct', parseFloat(v))} min={0} max={100} precision={2}>
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                      </HStack>
                      <FormControl><FormLabel fontSize="11px">Pension Provider</FormLabel><Input size="sm" value={f.pension_provider} onChange={e => set('pension_provider', e.target.value)} placeholder="e.g. NEST, The People's Pension" /></FormControl>
                      <FormControl><FormLabel fontSize="11px">Employee Pension Reference</FormLabel><Input size="sm" value={f.pension_ref} onChange={e => set('pension_ref', e.target.value)} /></FormControl>
                    </>
                  )}
                </VStack>
              </TabPanel>
              {/* Bank Account */}
              <TabPanel px={0}>
                <FormControl>
                  <FormLabel fontSize="11px">Pay Into Bank Account</FormLabel>
                  <Select size="sm" value={f.bank_account_id || ''} onChange={e => set('bank_account_id', e.target.value)}>
                    <option value="">— None selected —</option>
                    {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.bank_name} — {b.sort_code} / {b.account_number} ({b.account_holder})</option>)}
                  </Select>
                  {bankAccounts.length === 0 && f.contact_id && (
                    <Text fontSize="9px" color="gray.400" mt={1}>No bank accounts for this employee. Add them in Contacts → Bank Accounts.</Text>
                  )}
                </FormControl>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Pay Run Form ──────────────────────────────────────────────────
function PayRunForm({ isOpen, onClose, onSaved }) {
  const now = new Date();
  const BLANK = {
    name: `${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()} Payroll`,
    pay_date: new Date(now.getFullYear(), now.getMonth(), 25).toISOString().split('T')[0],
    period_start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
    period_end: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0],
    auto_populate: true, notes: '',
  };
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => { if (isOpen) setForm(BLANK); }, [isOpen]); // eslint-disable-line

  const save = async () => {
    if (!form.name || !form.pay_date) { toast({ title: 'Name and pay date required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      await api.post('/salary/pay-runs', form);
      toast({ title: 'Pay run created', status: 'success', duration: 1500 });
      onSaved(); onClose();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay /><ModalContent>
        <ModalHeader fontSize="md">Create Pay Run</ModalHeader><ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <FormControl isRequired><FormLabel fontSize="11px">Name</FormLabel><Input size="sm" value={form.name} onChange={e => set('name', e.target.value)} /></FormControl>
            <HStack w="100%">
              <FormControl isRequired><FormLabel fontSize="11px">Pay Date</FormLabel><Input size="sm" type="date" value={form.pay_date} onChange={e => set('pay_date', e.target.value)} /></FormControl>
            </HStack>
            <HStack w="100%">
              <FormControl><FormLabel fontSize="11px">Period Start</FormLabel><Input size="sm" type="date" value={form.period_start} onChange={e => set('period_start', e.target.value)} /></FormControl>
              <FormControl><FormLabel fontSize="11px">Period End</FormLabel><Input size="sm" type="date" value={form.period_end} onChange={e => set('period_end', e.target.value)} /></FormControl>
            </HStack>
            <HStack><Switch size="sm" colorScheme="blue" isChecked={form.auto_populate} onChange={e => set('auto_populate', e.target.checked)} /><Text fontSize="11px">Auto-populate from active employees</Text></HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Employer Summary Cards ────────────────────────────────────────
function EmployerSummary() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get('/salary/employer-summary').then(r => setSummary(r.data)).catch(() => {});
  }, []);
  if (!summary) return null;
  const m = summary.monthly;
  const a = summary.annual;
  const cards = [
    { label: 'Gross Payroll/mo', value: m.gross, color: 'blue' },
    { label: 'PAYE Tax/mo', value: m.paye_tax, color: 'red' },
    { label: 'Employee NI/mo', value: m.ee_ni, color: 'orange' },
    { label: 'Employer NI/mo', value: m.er_ni, color: 'orange' },
    { label: 'Pension (EE)/mo', value: m.ee_pension, color: 'purple' },
    { label: 'Pension (ER)/mo', value: m.er_pension, color: 'purple' },
    { label: 'Total Employer Cost/mo', value: m.total_employer_cost, color: 'red' },
    { label: 'Annual Payroll Cost', value: a.total_employer_cost, color: 'gray' },
  ];
  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
      {cards.map(c => (
        <Box key={c.label} p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" bg="white">
          <Text fontSize="9px" color="gray.500" textTransform="uppercase" fontWeight="600">{c.label}</Text>
          <Text fontSize="16px" fontWeight="800" color={`${c.color}.600`}>{fmtK(c.value)}</Text>
          <Text fontSize="9px" color="gray.400">{summary.employee_count} employees</Text>
        </Box>
      ))}
    </SimpleGrid>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────
export default function SalaryDashboard({ onBack, onOpenPension, onOpenEmployeeTax }) {
  const [employees, setEmployees] = useState([]);
  const [payRuns, setPayRuns] = useState([]);
  const [users, setUsers] = useState([]);
  const [payRunItems, setPayRunItems] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIdx, setTabIdx] = useState(0);
  const empForm = useDisclosure();
  const payRunForm = useDisclosure();
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, runsRes, conRes] = await Promise.all([
        api.get('/salary'),
        api.get('/salary/pay-runs'),
        api.get('/users'),
      ]);
      setEmployees(empRes.data);
      setPayRuns(runsRes.data);
      setUsers(conRes.data);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openRun = async (run) => {
    setSelectedRun(run);
    try {
      const { data } = await api.get(`/salary/pay-runs/${run.id}/items`);
      setPayRunItems(data);
    } catch(e) {}
    setTabIdx(2);
  };

  const updateRunStatus = async (status) => {
    await api.put(`/salary/pay-runs/${selectedRun.id}`, { status });
    fetchAll();
    setSelectedRun(p => ({ ...p, status }));
    toast({ title: `Pay run marked as ${status}`, status: 'success', duration: 1500 });
  };

  const activeEmployees = employees.filter(e => e.is_active);

  return (
    <Box p={4} maxW="1400px" mx="auto">
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft />} onClick={onBack}>Back</Button>
          <FiDollarSign size={18} />
          <Text fontWeight="700" fontSize="16px">Salary & Payroll</Text>
          <Badge colorScheme="blue">{activeEmployees.length} active employees</Badge>
        </HStack>
        <HStack>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={fetchAll}>Refresh</Button>
          {onOpenPension && <Button size="sm" colorScheme="purple" variant="outline" onClick={onOpenPension}>🏦 Pension</Button>}
          {onOpenEmployeeTax && <Button size="sm" colorScheme="red" variant="outline" onClick={onOpenEmployeeTax}>📋 Employee Tax</Button>}
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={() => { setEditRecord(null); empForm.onOpen(); }}>Add Employee</Button>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="green" onClick={payRunForm.onOpen}>New Pay Run</Button>
        </HStack>
      </Flex>

      {loading ? <Flex justify="center" py={12}><Spinner size="lg" /></Flex> : (
        <>
          <EmployerSummary />

          <Tabs index={tabIdx} onChange={setTabIdx}>
            <TabList>
              <Tab fontSize="12px"><HStack><FiUser size={12} /><Text>Employees ({employees.length})</Text></HStack></Tab>
              <Tab fontSize="12px"><FiInfo size={12} /> Tax Codes</Tab>
              <Tab fontSize="12px"><HStack><FiList size={12} /><Text>Pay Runs ({payRuns.length})</Text></HStack></Tab>
              {selectedRun && <Tab fontSize="12px"><FiList size={12} /> {selectedRun.name}</Tab>}
            </TabList>
            <TabPanels>

              {/* ── Employees ── */}
              <TabPanel px={0}>
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th fontSize="9px">Employee</Th>
                        <Th fontSize="9px">Tax Code</Th>
                        <Th fontSize="9px">NI</Th>
                        <Th fontSize="9px" isNumeric>Gross Annual</Th>
                        <Th fontSize="9px" isNumeric>Monthly Gross</Th>
                        <Th fontSize="9px">Pension</Th>
                        <Th fontSize="9px">Frequency</Th>
                        <Th fontSize="9px">Status</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {employees.length === 0 && (
                        <Tr><Td colSpan={9} textAlign="center" py={8} color="gray.400" fontSize="12px">No salary records. Add employees to get started.</Td></Tr>
                      )}
                      {employees.map(e => (
                        <Tr key={e.id} opacity={e.is_active ? 1 : 0.5}>
                          <Td>
                            <Text fontSize="12px" fontWeight="600">{e.employee_name || `${e.first_name} ${e.last_name}`}</Text>
                            <Text fontSize="9px" color="gray.400">{e.email}</Text>
                          </Td>
                          <Td><Badge colorScheme="purple" fontSize="9px">{e.tax_code}</Badge></Td>
                          <Td><Badge colorScheme="gray" fontSize="9px">Cat {e.ni_category}</Badge></Td>
                          <Td isNumeric fontSize="12px" fontWeight="700">{fmt(e.gross_annual)}</Td>
                          <Td isNumeric fontSize="11px">{fmt(parseFloat(e.gross_annual||0)/12)}</Td>
                          <Td>
                            {e.auto_enrolled ? (
                              <Text fontSize="10px">{e.pension_employee_pct}% / {e.pension_employer_pct}%</Text>
                            ) : <Text fontSize="9px" color="gray.400">Opted out</Text>}
                          </Td>
                          <Td fontSize="10px" textTransform="capitalize">{(e.pay_frequency||'').replace('_','-')}</Td>
                          <Td><Badge colorScheme={e.is_active ? 'green' : 'gray'} fontSize="8px">{e.is_active ? 'Active' : 'Inactive'}</Badge></Td>
                          <Td><IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => { setEditRecord(e); empForm.onOpen(); }} aria-label="Edit" /></Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </TabPanel>

              {/* ── Tax Codes ── */}
              <TabPanel px={0}>
                <Text fontSize="11px" color="gray.500" mb={3}>Current tax codes for all employees. All changes are logged with effective dates.</Text>
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th fontSize="9px">Employee</Th>
                        <Th fontSize="9px">Current Tax Code</Th>
                        <Th fontSize="9px">NI Category</Th>
                        <Th fontSize="9px">Start Date</Th>
                        <Th fontSize="9px">Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {employees.map(e => (
                        <Tr key={e.id}>
                          <Td fontSize="12px" fontWeight="600">{e.employee_name || `${e.first_name} ${e.last_name}`}</Td>
                          <Td><Badge colorScheme="purple" fontSize="10px" px={2}>{e.tax_code}</Badge></Td>
                          <Td><Badge colorScheme="blue" fontSize="10px">Category {e.ni_category}</Badge></Td>
                          <Td fontSize="11px">{e.start_date ? new Date(e.start_date+'T12:00:00').toLocaleDateString('en-GB') : '—'}</Td>
                          <Td><Badge colorScheme={e.is_active ? 'green' : 'gray'} fontSize="8px">{e.is_active ? 'Active' : 'Left'}</Badge></Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                <Alert status="info" mt={3} fontSize="11px" borderRadius="md">
                  <AlertIcon />
                  To update a tax code, edit the employee record. All changes are logged with effective dates automatically. HMRC P45/P60 integration is a future enhancement.
                </Alert>
              </TabPanel>

              {/* ── Pay Runs ── */}
              <TabPanel px={0}>
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th fontSize="9px">Pay Run</Th>
                        <Th fontSize="9px">Pay Date</Th>
                        <Th fontSize="9px">Period</Th>
                        <Th fontSize="9px" isNumeric>Emps</Th>
                        <Th fontSize="9px" isNumeric>Gross</Th>
                        <Th fontSize="9px" isNumeric>Tax</Th>
                        <Th fontSize="9px" isNumeric>NI (EE)</Th>
                        <Th fontSize="9px" isNumeric>Pension</Th>
                        <Th fontSize="9px" isNumeric>Net Pay</Th>
                        <Th fontSize="9px">Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {payRuns.length === 0 && (
                        <Tr><Td colSpan={10} textAlign="center" py={8} color="gray.400" fontSize="12px">No pay runs yet.</Td></Tr>
                      )}
                      {payRuns.map(r => (
                        <Tr key={r.id} cursor="pointer" _hover={{ bg: 'blue.50' }} onClick={() => openRun(r)}>
                          <Td fontSize="12px" fontWeight="600" color="blue.600">{r.name}</Td>
                          <Td fontSize="11px">{r.pay_date ? new Date(r.pay_date+'T12:00:00').toLocaleDateString('en-GB') : '—'}</Td>
                          <Td fontSize="9px" color="gray.500">{r.period_start?.split('T')[0]} → {r.period_end?.split('T')[0]}</Td>
                          <Td isNumeric fontSize="11px">{r.employee_count || 0}</Td>
                          <Td isNumeric fontSize="11px">{fmtK(r.total_gross)}</Td>
                          <Td isNumeric fontSize="11px" color="red.600">{fmtK(r.total_tax)}</Td>
                          <Td isNumeric fontSize="11px" color="orange.600">{fmtK(r.total_ni)}</Td>
                          <Td isNumeric fontSize="11px" color="purple.600">{fmtK(r.total_net - (r.total_gross - r.total_tax - r.total_ni))}</Td>
                          <Td isNumeric fontSize="12px" fontWeight="700" color="green.700">{fmtK(r.total_net)}</Td>
                          <Td><Badge colorScheme={STATUS_COLOUR[r.status]} fontSize="8px" textTransform="capitalize">{r.status}</Badge></Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </TabPanel>

              {/* ── Pay Run Detail ── */}
              {selectedRun && (
                <TabPanel px={0}>
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack>
                      <Text fontWeight="700" fontSize="14px">{selectedRun.name}</Text>
                      <Badge colorScheme={STATUS_COLOUR[selectedRun.status]} textTransform="capitalize">{selectedRun.status}</Badge>
                    </HStack>
                    <HStack>
                      {selectedRun.status === 'draft' && <Button size="xs" colorScheme="blue" onClick={() => updateRunStatus('processing')}>Mark Processing</Button>}
                      {selectedRun.status === 'processing' && <Button size="xs" colorScheme="green" leftIcon={<FiCheck />} onClick={() => updateRunStatus('completed')}>Mark Completed</Button>}
                    </HStack>
                  </Flex>
                  <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                    <Table size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th fontSize="9px">Employee</Th>
                          <Th fontSize="9px" isNumeric>Gross</Th>
                          <Th fontSize="9px" isNumeric>PAYE Tax</Th>
                          <Th fontSize="9px" isNumeric>EE NI</Th>
                          <Th fontSize="9px" isNumeric>EE Pension</Th>
                          <Th fontSize="9px" isNumeric>Net Pay</Th>
                          <Th fontSize="9px" isNumeric>ER NI</Th>
                          <Th fontSize="9px" isNumeric>ER Pension</Th>
                          <Th fontSize="9px">Status / Payslip</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {payRunItems.length === 0 && <Tr><Td colSpan={9} textAlign="center" py={6} color="gray.400" fontSize="12px">No items.</Td></Tr>}
                        {payRunItems.map(item => (
                          <Tr key={item.id}>
                            <Td>
                              <Text fontSize="12px" fontWeight="600">{item.employee_name}</Text>
                              <Badge fontSize="7px" colorScheme="blue">{item.tax_code_used}</Badge>
                            </Td>
                            <Td isNumeric fontSize="11px">{fmt(item.gross_amount)}</Td>
                            <Td isNumeric fontSize="11px" color="red.600">{fmt(item.tax_amount)}</Td>
                            <Td isNumeric fontSize="11px" color="orange.600">{fmt(item.ni_amount)}</Td>
                            <Td isNumeric fontSize="11px" color="purple.600">{fmt(item.pension_amount)}</Td>
                            <Td isNumeric fontSize="12px" fontWeight="700" color="green.700">{fmt(item.net_amount)}</Td>
                            <Td isNumeric fontSize="10px" color="gray.500">{fmt(item.employer_ni_amount)}</Td>
                            <Td isNumeric fontSize="10px" color="gray.500">{fmt(item.employer_pension_amount)}</Td>
                            <Td>
                            <HStack spacing={1}>
                              <Badge colorScheme={item.status === 'paid' ? 'green' : 'gray'} fontSize="8px">{item.status}</Badge>
                              <Button size="xs" variant="ghost" colorScheme="purple"
                                onClick={() => {
                                  const url = `/api/salary/pay-runs/${selectedRun.id}/payslip/${item.id}`;
                                  const token = localStorage.getItem('token');
                                  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                                    .then(r => r.text())
                                    .then(html => { const w = window.open('','_blank'); w.document.write(html); w.document.close(); });
                                }}>Payslip</Button>
                            </HStack>
                          </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  {payRunItems.length > 0 && (
                    <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                      <SimpleGrid columns={4} spacing={3}>
                        {[
                          ['Total Gross', payRunItems.reduce((a,i) => a+parseFloat(i.gross_amount||0), 0), 'blue.700'],
                          ['Total PAYE', payRunItems.reduce((a,i) => a+parseFloat(i.tax_amount||0), 0), 'red.600'],
                          ['Total EE NI', payRunItems.reduce((a,i) => a+parseFloat(i.ni_amount||0), 0), 'orange.600'],
                          ['Total Net Pay', payRunItems.reduce((a,i) => a+parseFloat(i.net_amount||0), 0), 'green.700'],
                          ['Total ER NI', payRunItems.reduce((a,i) => a+parseFloat(i.employer_ni_amount||0), 0), 'gray.600'],
                          ['Total ER Pension', payRunItems.reduce((a,i) => a+parseFloat(i.employer_pension_amount||0), 0), 'gray.600'],
                          ['Total EE Pension', payRunItems.reduce((a,i) => a+parseFloat(i.pension_amount||0), 0), 'purple.600'],
                          ['Total Employer Cost', payRunItems.reduce((a,i) => a+parseFloat(i.gross_amount||0)+parseFloat(i.employer_ni_amount||0)+parseFloat(i.employer_pension_amount||0), 0), 'red.800'],
                        ].map(([label, val, color]) => (
                          <Box key={label} textAlign="center">
                            <Text fontSize="9px" color="gray.500">{label}</Text>
                            <Text fontSize="13px" fontWeight="700" color={color}>{fmt(val)}</Text>
                          </Box>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </>
      )}

      <EmployeeForm isOpen={empForm.isOpen} onClose={empForm.onClose} onSaved={fetchAll} editRecord={editRecord} contacts={contacts} />
      <PayRunForm isOpen={payRunForm.isOpen} onClose={payRunForm.onClose} onSaved={fetchAll} />
    </Box>
  );
}
